// src/services/storage/idb.js — DB v3
// Stores: api_cache, user_data, tile_registry, notification_history, sync_queue

import { captureIDBError } from '@services/monitoring';

const DB_NAME    = 'eyeonstorm';
const DB_VERSION = 3;
let _db = null;

export function openDB() {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (e) => {
      const db  = e.target.result;
      const old = e.oldVersion;

      if (old < 1) {
        const a = db.createObjectStore('api_cache', { keyPath: 'url' });
        a.createIndex('cachedAt', 'cachedAt');
        db.createObjectStore('user_data', { keyPath: 'key' });
        const t = db.createObjectStore('tile_registry', { keyPath: 'url' });
        t.createIndex('registeredAt', 'registeredAt');
        // legacy posts_queue (compat only — new writes use sync_queue)
        if (!db.objectStoreNames.contains('posts_queue')) {
          db.createObjectStore('posts_queue', { keyPath: 'id', autoIncrement: true });
        }
      }
      if (old < 2) {
        if (!db.objectStoreNames.contains('notification_history')) {
          db.createObjectStore('notification_history', { keyPath: 'id', autoIncrement: true });
        }
      }
      if (old < 3) {
        const sq = db.createObjectStore('sync_queue', { keyPath: 'syncId' });
        sq.createIndex('status',      'status');
        sq.createIndex('priority',    'priority');
        sq.createIndex('nextRetryAt', 'nextRetryAt');
        sq.createIndex('type',        'type');
        sq.createIndex('createdAt',   'createdAt');
      }
    };

    req.onsuccess = (e) => { _db = e.target.result; resolve(_db); };
    req.onerror   = (e) => reject(e.target.error);
  });
}


// ── IDB error handler ────────────────────────────────────────────
// Called by every primitive on failure.
// Detects QuotaExceededError and attempts recovery by clearing
// the tile cache (largest store) before re-throwing.
// All other errors are logged to Sentry and degraded gracefully.
const _quotaRecoveryAttempted = { done: false };

async function _handleIDBError(op, store, err) {
  const isQuota = err?.name === 'QuotaExceededError'
               || err?.message?.includes('quota')
               || err?.message?.includes('storage');

  captureIDBError(op, store, err?.message ?? String(err));

  if (isQuota && !_quotaRecoveryAttempted.done) {
    _quotaRecoveryAttempted.done = true;
    console.warn('[IDB] QuotaExceededError — clearing tile cache to recover');
    try {
      // Wipe the largest store (tile_registry + SW tile cache)
      const db  = await openDB();
      const txn = db.transaction('tile_registry', 'readwrite');
      txn.objectStore('tile_registry').clear();

      // Also ask the SW to clear tile cache
      if (typeof navigator !== 'undefined' && navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_TILE_CACHE' });
      }

      // Notify the UI so the user knows what happened
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('eos-storage-recovered', {
          detail: { freed: 'tile_cache' }
        }));
      }
    } catch {}
    _quotaRecoveryAttempted.done = false;  // allow retry next session
  }
}

// ── Primitives ────────────────────────────────────────────────────
export async function txGet(store, key) {
  try {
    const db  = await openDB();
    const req = db.transaction(store, 'readonly').objectStore(store).get(key);
    return await new Promise((res, rej) => {
      req.onsuccess = () => res(req.result ?? null);
      req.onerror   = () => rej(req.error);
    });
  } catch (err) {
    _handleIDBError('get', store, err);
    return null;   // degrade gracefully — caller gets null, not a crash
  }
}

export async function txGetAll(store, idx, query) {
  try {
    const db  = await openDB();
    const s   = db.transaction(store, 'readonly').objectStore(store);
    const req = idx ? s.index(idx).getAll(query) : s.getAll();
    return await new Promise((res, rej) => {
      req.onsuccess = () => res(req.result);
      req.onerror   = () => rej(req.error);
    });
  } catch (err) {
    _handleIDBError('getAll', store, err);
    return [];   // empty array — callers handle empty gracefully
  }
}

export async function txPut(store, value) {
  try {
    const db  = await openDB();
    const txn = db.transaction(store, 'readwrite');
    const req = txn.objectStore(store).put(value);
    return await new Promise((res, rej) => {
      txn.oncomplete = () => res(req.result);
      txn.onerror    = () => {
        captureIDBError('put', store, txn.error?.message);
        rej(txn.error);
      };
      txn.onabort = () => rej(new Error('IDB tx aborted'));
    });
  } catch (err) {
    await _handleIDBError('put', store, err);
    // Re-throw for sync_queue writes — caller must know if SOS failed to persist
    if (store === 'sync_queue') throw err;
    // Silent degrade for caches — a missed cache write is not fatal
  }
}

export async function txDelete(store, key) {
  try {
    const db  = await openDB();
    const txn = db.transaction(store, 'readwrite');
    txn.objectStore(store).delete(key);
    return await new Promise((res, rej) => {
      txn.oncomplete = () => res();
      txn.onerror    = () => {
        captureIDBError('delete', store, txn.error?.message);
        rej(txn.error);
      };
    });
  } catch (err) {
    _handleIDBError('delete', store, err);
    // Non-fatal — failed deletes leave stale data, not missing data
  }
}

export async function txCount(store) {
  try {
    const db  = await openDB();
    const req = db.transaction(store, 'readonly').objectStore(store).count();
    return await new Promise((res, rej) => {
      req.onsuccess = () => res(req.result);
      req.onerror   = () => rej(req.error);
    });
  } catch (err) {
    _handleIDBError('count', store, err);
    return 0;
  }
}

// ── SyncQueue ─────────────────────────────────────────────────────
// Fields per item:
//   syncId, type, priority, status (pending|syncing|done|dead),
//   payload, version (monotonic ms), attempts, maxAttempts,
//   lastError, nextRetryAt, createdAt, updatedAt, resolvedAt
export const SyncQueue = {

  async add(item) {
    const now    = Date.now();
    const record = {
      syncId:      item.syncId      ?? _uuid(),
      type:        item.type        ?? 'create_report',
      priority:    item.priority    ?? _priorityFor(item.payload),
      status:      'pending',
      payload:     item.payload,
      version:     item.version     ?? now,
      attempts:    0,
      maxAttempts: item.maxAttempts ?? _maxAttempts(item.type),
      lastError:   null,
      nextRetryAt: now,
      createdAt:   now,
      updatedAt:   now,
      resolvedAt:  null,
    };
    await txPut('sync_queue', record);
    return record;
  },

  // On startup: reset any items left in 'syncing' state (crashed mid-attempt).
  async resetSyncing() {
    const all      = await txGetAll('sync_queue');
    const stuck    = all.filter((i) => i.status === 'syncing');
    const now      = Date.now();
    await Promise.all(stuck.map((i) =>
      txPut('sync_queue', {
        ...i,
        status:      'pending',
        nextRetryAt: now + 5_000,   // short delay before retrying
        updatedAt:   now,
      })
    ));
    return stuck.length;
  },

  async getPending() {
    const now = Date.now();
    const all = await txGetAll('sync_queue', 'status', IDBKeyRange.only('pending'));
    return all
      .filter((i) => i.nextRetryAt <= now)
      .sort((a, b) => b.priority - a.priority || a.createdAt - b.createdAt);
  },

  async get(syncId)    { return txGet('sync_queue', syncId); },
  async getAll()       { return txGetAll('sync_queue'); },
  async remove(syncId) { return txDelete('sync_queue', syncId); },

  async markSyncing(syncId) {
    const i = await SyncQueue.get(syncId);
    if (i) await txPut('sync_queue', { ...i, status: 'syncing', updatedAt: Date.now() });
  },

  async markDone(syncId) {
    const i = await SyncQueue.get(syncId);
    if (i) await txPut('sync_queue', {
      ...i, status: 'done', resolvedAt: Date.now(), updatedAt: Date.now(),
    });
  },

  async markFailed(syncId, errorMsg) {
    const i = await SyncQueue.get(syncId);
    if (!i) return { dead: false, attempts: 0, backoffMs: 0 };
    const attempts = i.attempts + 1;
    const dead     = attempts >= i.maxAttempts;
    // Exponential backoff: 5s → 15s → 45s → 2m 15s → 6m 45s … cap 10m
    const backoff  = Math.min(10 * 60_000, 5_000 * Math.pow(3, attempts - 1));
    await txPut('sync_queue', {
      ...i,
      status:      dead ? 'dead' : 'pending',
      attempts,
      lastError:   errorMsg,
      nextRetryAt: Date.now() + backoff,
      resolvedAt:  dead ? Date.now() : null,
      updatedAt:   Date.now(),
    });
    return { dead, attempts, backoffMs: backoff };
  },

  async pruneDone(maxAgeMs = 24 * 60 * 60_000) {
    const all    = await txGetAll('sync_queue', 'status', IDBKeyRange.only('done'));
    const cutoff = Date.now() - maxAgeMs;
    const old    = all.filter((i) => (i.resolvedAt ?? 0) < cutoff);
    await Promise.all(old.map((i) => txDelete('sync_queue', i.syncId)));
    return old.length;
  },

  async countByStatus() {
    const all = await txGetAll('sync_queue');
    const out = { pending: 0, syncing: 0, done: 0, dead: 0 };
    all.forEach((i) => { out[i.status] = (out[i.status] ?? 0) + 1; });
    return out;
  },
};

// ── APICache ──────────────────────────────────────────────────────
export const APICache = {
  async set(url, data, ttlMs = 3 * 60_000) {
    if (!url) return;
    await txPut('api_cache', {
      url, data, cachedAt: Date.now(), expiresAt: Date.now() + ttlMs,
    });
  },

  async get(url) {
    if (!url) return null;
    const e = await txGet('api_cache', url);
    if (!e) return null;
    return { data: e.data, stale: Date.now() > e.expiresAt, cachedAt: e.cachedAt };
  },

  async invalidate(url) { if (url) await txDelete('api_cache', url); },

  async prune() {
    const db  = await openDB();
    const now = Date.now();
    return new Promise((res) => {
      const txn  = db.transaction('api_cache', 'readwrite');
      const idx  = txn.objectStore('api_cache').index('cachedAt');
      const req  = idx.openCursor();
      let n = 0;
      req.onsuccess = (ev) => {
        const c = ev.target.result;
        if (!c) return res(n);
        if (c.value.expiresAt < now) { c.delete(); n++; }
        c.continue();
      };
      req.onerror = () => res(n);
    });
  },
};

// ── UserData — PII backup (never sent to server) ──────────────────
export const UserData = {
  async set(key, value) {
    await txPut('user_data', { key, value, updatedAt: Date.now() });
  },
  async get(key)    { return (await txGet('user_data', key))?.value ?? null; },
  async remove(key) { return txDelete('user_data', key); },
};

// ── TileRegistry ──────────────────────────────────────────────────
export const TileRegistry = {
  async register(url) {
    await txPut('tile_registry', { url, registeredAt: Date.now() });
  },
  async count() { return txCount('tile_registry'); },
  async evictLRU(count) {
    try {
      const all    = await idbGetAll(TILE_REG_STORE);
      const sorted = all.sort((a, b) => (a.accessedAt ?? 0) - (b.accessedAt ?? 0));
      const toEvict = sorted.slice(0, count);
      for (const item of toEvict) {
        await idbDel(TILE_REG_STORE, item.url);
      }
      return toEvict.length;
    } catch { return 0; }
  },

  async evictOld(maxAgeMs = 30 * 24 * 60 * 60_000) {
    const all    = await txGetAll('tile_registry');
    const cutoff = Date.now() - maxAgeMs;
    const old    = all.filter((t) => t.registeredAt < cutoff);
    await Promise.all(old.map((t) => txDelete('tile_registry', t.url)));
    return old.length;
  },
};

// ── NotificationHistory ───────────────────────────────────────────
export const NotificationHistory = {
  async add(notif) {
    await txPut('notification_history', { ...notif, id: _uuid(), receivedAt: Date.now() });
    // Keep last 50
    const all = await txGetAll('notification_history');
    if (all.length > 50) {
      const oldest = all.sort((a, b) => a.receivedAt - b.receivedAt).slice(0, all.length - 50);
      await Promise.all(oldest.map((n) => txDelete('notification_history', n.id)));
    }
  },
  async getAll() {
    const all = await txGetAll('notification_history');
    return all.sort((a, b) => b.receivedAt - a.receivedAt);
  },
};

// ── Verification Score Cache ──────────────────────────────────────
// Caches the 100-point breakdown for each report to avoid re-fetching
// from Firestore on every scroll. Key: reportId, TTL: 10 minutes.
const VER_CACHE_STORE  = 'verification_cache';
const VER_CACHE_TTL_MS = 10 * 60_000;

export const VerificationCache = {
  async get(reportId) {
    try {
      const entry = await idbGet(VER_CACHE_STORE, reportId);
      if (!entry) return null;
      if (Date.now() - entry.cachedAt > VER_CACHE_TTL_MS) {
        idbDel(VER_CACHE_STORE, reportId).catch(() => {});
        return null;
      }
      return entry.data;
    } catch { return null; }
  },
  async set(reportId, data) {
    try { await idbSet(VER_CACHE_STORE, reportId, { data, cachedAt: Date.now() }); } catch {}
  },
  async bust(reportId) {
    try { await idbDel(VER_CACHE_STORE, reportId); } catch {}
  },
};

// ── Stats ─────────────────────────────────────────────────────────
export async function getDBStats() {
  const [sync, tiles] = await Promise.all([
    SyncQueue.countByStatus(),
    TileRegistry.count(),
  ]);
  return { sync, tiles };
}

// ── Private helpers ───────────────────────────────────────────────
function _uuid() {
  return crypto?.randomUUID?.() ??
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
}

function _priorityFor(payload) {
  const t = payload?.type ?? '';
  return (
    { sos: 5, flood: 4, fire: 4, hazmat: 4, medical: 4, road: 3, power: 3, shelter: 2, gas: 2 }[t] ?? 1
  );
}

function _maxAttempts(opType) {
  return (
    { create_sos: 10, create_report: 5, update_gas: 5, confirm_report: 3, flag_report: 3 }[opType] ?? 5
  );
}
